import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Sparkles, X, Send, Mic, MicOff, Phone, Calendar, Loader2, MessageSquare } from "lucide-react";
import { trackLeadEvent } from "@/components/Analytics";
import { buildAttribution } from "@/lib/attribution";
import { SEARCH_MODE } from "@/lib/searchConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "ozy_chat_session";
const CONSENT_KEY = "ozy_chat_consent";

const SUGGESTIONS_INITIAL = [
  "I need a commercial loan",
  "I want to refinance",
  "How much can I borrow?",
  "I have a private loan maturing",
];

const WELCOME = "Hey, I'm OZY — I organise your finance search for the OzyLoans team. What are you looking to fund?";

export default function ChatAssistant() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: WELCOME }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [suggestions, setSuggestions] = useState(SUGGESTIONS_INITIAL);
  const [consented, setConsented] = useState(() => !!localStorage.getItem(CONSENT_KEY));
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [listening, setListening] = useState(false);
  const [unread, setUnread] = useState(false);

  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Auto-open after first scroll engagement
  useEffect(() => {
    let opened = sessionStorage.getItem("ozy_chat_pulsed");
    if (opened) return;
    const onScroll = () => {
      if (window.scrollY > 600) {
        setUnread(true);
        sessionStorage.setItem("ozy_chat_pulsed", "1");
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const sendMessage = useCallback(async (text) => {
    if (!text || sending) return;
    if (!consented) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { text, session_id: sessionId || undefined });
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
        sessionStorage.setItem(STORAGE_KEY, data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      setSuggestions(data.suggested_replies?.length ? data.suggested_replies : []);

      // Heuristic: if the bot is asking for contact, offer the inline form
      const r = (data.reply || "").toLowerCase();
      if (/share your|callback|submit your details|name.*phone|phone.*email/.test(r)) {
        setShowLeadForm(true);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "I'm having trouble right now. Please try again shortly, or request a human review and a finance professional will contact you.";
      setMessages((m) => [...m, { role: "assistant", text: msg, error: true }]);
    } finally {
      setSending(false);
    }
  }, [sending, sessionId, consented]);

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return alert("Voice input isn't supported in this browser. Try Chrome.");
    const rec = new SR();
    rec.lang = navigator.language || "en-AU";
    rec.continuous = false;
    rec.interimResults = false;
    rec.onresult = (e) => {
      const text = e.results[0][0].transcript;
      setInput((v) => (v ? v + " " : "") + text);
      inputRef.current?.focus();
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    rec.start();
    recognitionRef.current = rec;
    setListening(true);
  };
  const stopVoice = () => { recognitionRef.current?.stop(); setListening(false); };

  const giveConsent = () => {
    localStorage.setItem(CONSENT_KEY, "1");
    setConsented(true);
  };

  const handleOpen = () => {
    setOpen(true);
    setMinimized(false);
    setUnread(false);
    if (typeof window !== "undefined" && window.gtag) window.gtag("event", "chat_open");
  };

  // /search "Continue Funding Search" hands the query to OZY (chat lives outside the router)
  useEffect(() => {
    const onOpen = (e) => {
      handleOpen();
      const seed = e?.detail?.seed;
      if (seed) setInput(seed);
    };
    window.addEventListener("ozy:open-chat", onOpen);
    return () => window.removeEventListener("ozy:open-chat", onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastUserText = [...messages].reverse().find((m) => m.role === "user")?.text || "";
  const searchHref = SEARCH_MODE && lastUserText.length > 5 ? `/search?q=${encodeURIComponent(lastUserText.slice(0, 300))}` : "";

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={handleOpen}
          data-testid="chat-launcher"
          aria-label="Open AI finance assistant"
          className="fixed bottom-5 md:bottom-6 left-4 md:left-6 z-50 group"
        >
          <span className="absolute inset-0 rounded-full bg-[#E50914] opacity-30 blur-xl group-hover:opacity-50 transition-opacity" />
          <span className="relative flex items-center gap-2 h-14 pl-4 pr-5 rounded-full bg-gradient-to-br from-[#1a1a1a] via-[#0c0c0c] to-[#0a0a0a] border border-[#E50914]/40 hover:border-[#E50914] shadow-[0_10px_40px_rgba(0,0,0,0.6)] transition-all">
            <span className="relative flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-[#FF3B47] via-[#E50914] to-[#A50710] shadow-inner">
              <Sparkles className="w-4 h-4 text-black" />
              {unread && <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#E50914] ring-2 ring-black animate-pulse" />}
            </span>
            <span className="text-white text-sm font-medium hidden sm:inline">Ask OZY</span>
            <span className="text-[10px] uppercase tracking-widest text-[#E50914] hidden md:inline">AI</span>
          </span>
        </button>
      )}

      {/* Panel */}
      <div
        data-testid="chat-panel"
        className={`fixed z-50 transition-all duration-300 ${
          open && !minimized
            ? "bottom-0 right-0 left-0 md:bottom-6 md:left-6 md:right-auto md:w-[420px] md:h-[640px] h-[85dvh] md:rounded-2xl rounded-t-3xl"
            : "pointer-events-none opacity-0 translate-y-4"
        } bg-[#070707]/95 backdrop-blur-2xl border border-[#E50914]/25 shadow-[0_30px_80px_rgba(0,0,0,0.8),0_0_0_1px_rgba(212,175,55,0.08)] flex flex-col overflow-hidden`}
        style={{ display: open ? "flex" : "none" }}
      >
        {/* Decorative gold gradient */}
        <div aria-hidden="true" className="absolute -top-32 -right-32 w-72 h-72 rounded-full pointer-events-none opacity-20"
             style={{ background: "radial-gradient(closest-side, #E50914, transparent 70%)" }} />

        {/* Header */}
        <header className="relative flex items-center gap-3 px-5 py-4 border-b border-zinc-800/80">
          <div className="relative flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-[#FF3B47] via-[#E50914] to-[#A50710] shadow-inner shrink-0">
            <Sparkles className="w-5 h-5 text-black" />
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-black" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-bebas text-xl tracking-wide text-white">OZY</h3>
              <span className="text-[9px] uppercase tracking-[0.2em] text-[#E50914] bg-[#E50914]/10 border border-[#E50914]/30 rounded-full px-2 py-0.5">AI</span>
            </div>
            <p className="text-[11px] text-zinc-500 leading-tight">OzyLoans finance assistant · online</p>
          </div>
          <button onClick={() => setOpen(false)} aria-label="Close" className="p-1.5 text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Body */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-3 scroll-elegant" data-testid="chat-body">
          {messages.map((m, i) => (
            <Bubble key={i} role={m.role} text={m.text} error={m.error} />
          ))}
          {sending && <TypingDots />}
          {showLeadForm && consented && (
            <InlineLeadForm
              sessionId={sessionId}
              onDone={(score) => {
                setShowLeadForm(false);
                setMessages((m) => [...m, {
                  role: "assistant",
                  text: `Thanks — submitted (priority ${score}/100). A specialist will reach out shortly. In the meantime, anything else I can help with?`,
                }]);
              }}
            />
          )}
        </div>

        {/* Suggestions */}
        {!sending && suggestions.length > 0 && (
          <div className="px-4 pb-2 flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => sendMessage(s)}
                disabled={!consented}
                data-testid="chat-suggestion"
                className="text-xs text-zinc-300 bg-[#111] hover:bg-[#1a1a1a] border border-zinc-800 hover:border-[#E50914]/40 rounded-full px-3 py-1.5 transition-colors disabled:opacity-50"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Tools row */}
        {consented && (
          <div className="px-4 py-2 flex items-center justify-between gap-2 border-t border-zinc-900">
            <div className="flex items-center gap-1">
              {process.env.REACT_APP_CALENDLY_URL && (
                <a
                  href={process.env.REACT_APP_CALENDLY_URL}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-[#E50914] px-2 py-1"
                >
                  <Calendar className="w-3 h-3" /> Book
                </a>
              )}
              <button
                type="button"
                onClick={() => setShowLeadForm(true)}
                className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-[#E50914] px-2 py-1"
              >
                <MessageSquare className="w-3 h-3" /> Submit details
              </button>
              <a
                href="tel:+61426976876"
                className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-[#E50914] px-2 py-1"
              >
                <Phone className="w-3 h-3" /> Call
              </a>
            </div>
            <span className="text-[10px] text-zinc-600">Auto-detects your language</span>
          </div>
        )}

        {/* Composer */}
        <form
          onSubmit={(e) => { e.preventDefault(); sendMessage(input.trim()); }}
          className="relative px-4 py-3 border-t border-zinc-800/80 bg-[#050505]/90"
        >
          {!consented ? (
            <ConsentGate onConsent={giveConsent} />
          ) : (
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={listening ? stopVoice : startVoice}
                aria-label={listening ? "Stop voice input" : "Start voice input"}
                className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center border transition-all ${
                  listening
                    ? "bg-[#E50914] border-[#E50914] text-white animate-pulse"
                    : "bg-[#111] border-zinc-800 text-zinc-400 hover:text-[#E50914] hover:border-[#E50914]/40"
                }`}
              >
                {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(input.trim());
                  }
                }}
                placeholder="Ask anything — funding, eligibility, refinance…"
                rows={1}
                data-testid="chat-input"
                className="flex-1 resize-none max-h-32 bg-[#0c0c0c] border border-zinc-800 focus:border-[#E50914]/60 rounded-2xl px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#E50914]/40"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                aria-label="Send"
                data-testid="chat-send"
                className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center bg-gradient-to-br from-[#FF3B47] via-[#E50914] to-[#A50710] text-black disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_4px_15px_rgba(212,175,55,0.4)]"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          )}

          <p className="text-[10px] text-zinc-600 text-center mt-2 leading-snug">
            {searchHref && (
              <a href={searchHref} data-testid="chat-search-link" className="block mb-1.5 text-[11px] text-[#E50914] hover:underline">
                See funding pathways for this in the OzyLoans finance search →
              </a>
            )}
            AI-generated · indicative only · we may receive commissions from lenders. <a href="/credit-guide" className="underline hover:text-[#E50914]">Credit Guide</a> · <a href="/privacy" className="underline hover:text-[#E50914]">Privacy</a>
          </p>
        </form>
      </div>
    </>
  );
}

// ============ Sub-components ============
function Bubble({ role, text, error }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-ph-mask>
      {!isUser && (
        <div className="w-7 h-7 rounded-full mr-2 mt-0.5 shrink-0 bg-gradient-to-br from-[#FF3B47] via-[#E50914] to-[#A50710] flex items-center justify-center">
          <Sparkles className="w-3.5 h-3.5 text-black" />
        </div>
      )}
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-[#E50914] text-white rounded-br-sm"
            : error
              ? "bg-[#2a0506] text-rose-200 border border-rose-900/50 rounded-bl-sm"
              : "bg-[#111] text-zinc-100 border border-zinc-800/70 rounded-bl-sm"
        }`}
      >
        {text}
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF3B47] via-[#E50914] to-[#A50710] flex items-center justify-center">
        <Sparkles className="w-3.5 h-3.5 text-black" />
      </div>
      <div className="bg-[#111] border border-zinc-800/70 rounded-2xl rounded-bl-sm px-4 py-3 inline-flex gap-1">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-bounce"
            style={{ animationDelay: `${i * 120}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

function ConsentGate({ onConsent }) {
  const [seconds, setSeconds] = useState(7);

  useEffect(() => {
    if (seconds <= 0) {
      onConsent();
      return undefined;
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds, onConsent]);

  const progress = ((7 - seconds) / 7) * 100;

  return (
    <div className="space-y-4" data-testid="chat-consent">
      <div className="relative overflow-hidden bg-gradient-to-br from-[#140709] via-[#0c0c0c] to-[#0a0a0a] border border-[#E50914]/30 rounded-2xl p-5">
        <div
          aria-hidden="true"
          className="absolute -top-20 -right-20 w-48 h-48 rounded-full pointer-events-none opacity-30"
          style={{ background: "radial-gradient(closest-side, rgba(229,9,20,0.45), rgba(229,9,20,0) 70%)" }}
        />
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF3B47] via-[#E50914] to-[#A50710] flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-black" />
            </div>
            <p className="font-bebas text-lg tracking-wide text-white leading-none">
              You&apos;re about to chat with OZY
            </p>
          </div>

          <p className="text-[12.5px] text-zinc-300 leading-relaxed">
            A couple of things worth knowing before we get started:
          </p>

          <ul className="mt-2 space-y-2 text-[12px] text-zinc-400 leading-relaxed">
            <li>
              By chatting here, you agree to our{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#E50914] hover:underline"
                data-testid="chat-consent-privacy-link"
              >
                Privacy Policy
              </a>{" "}
              and consent to receiving communications related to your enquiry.
            </li>
            <li>
              Like all AI assistants, OZY isn&apos;t perfect. Anything discussed should be treated as general information only and verified with the OzyLoans team during your call.
            </li>
          </ul>

          <div className="mt-4 flex items-center justify-between gap-3">
            <p className="text-[12px] text-zinc-300" data-testid="chat-consent-countdown">
              Connecting you in <span className="text-[#E50914] font-semibold tabular-nums">{seconds}</span> second{seconds === 1 ? "" : "s"}…
            </p>
            <button
              type="button"
              onClick={onConsent}
              data-testid="chat-consent-start"
              className="text-[11px] uppercase tracking-widest text-[#E50914] hover:text-white transition-colors"
            >
              Start chat now →
            </button>
          </div>

          <div className="mt-3 h-1 w-full rounded-full bg-zinc-800 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#FF3B47] via-[#E50914] to-[#A50710] transition-[width] duration-1000 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function InlineLeadForm({ sessionId, onDone }) {
  const [f, setF] = useState({
    name: "", phone: "", email: "",
    loan_type: "", loan_amount: "", timeline: "",
    income_range: "", credit_profile: "", description: "", consent: true,
  });
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/chat/lead`, { ...f, session_id: sessionId, source_channel: "ozy_chat", attribution: buildAttribution() });
      trackLeadEvent(f.loan_type || "chat", 0);
      onDone(data.score);
    } catch (err) {
      onDone(0);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="chat-lead-form" className="bg-[#0c0c0c] border border-[#E50914]/30 rounded-xl p-4 space-y-2.5">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-3.5 h-3.5 text-[#E50914]" />
        <span className="text-[11px] uppercase tracking-[0.25em] text-[#E50914] font-medium">Pre-qualify in 30 seconds</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <ChatLeadField label="Name" k="name" value={f.name} onChange={upd("name")} placeholder="Jane Doe" required />
        <ChatLeadField label="Phone" k="phone" value={f.phone} onChange={upd("phone")} placeholder="+61 4xx xxx xxx" required />
      </div>
      <ChatLeadField label="Email" k="email" type="email" value={f.email} onChange={upd("email")} placeholder="you@email.com" />
      <div className="grid grid-cols-2 gap-2">
        <ChatLeadField label="Loan type" k="loan_type" value={f.loan_type} onChange={upd("loan_type")} placeholder="Commercial / SMSF / Refi…" />
        <ChatLeadField label="Amount" k="loan_amount" value={f.loan_amount} onChange={upd("loan_amount")} placeholder="$500,000" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <ChatLeadField label="Timeline" k="timeline" value={f.timeline} onChange={upd("timeline")} placeholder="ASAP / 1–3 months" />
        <ChatLeadField label="Income range" k="income_range" value={f.income_range} onChange={upd("income_range")} placeholder="$150K–$300K p.a." />
      </div>
      <ChatLeadField label="Credit profile" k="credit_profile" value={f.credit_profile} onChange={upd("credit_profile")} placeholder="Clean / 1 default / unsure" />
      <button
        type="submit"
        disabled={busy || !f.name || !f.phone}
        className="w-full h-10 rounded-full bg-gradient-to-br from-[#FF3B47] via-[#E50914] to-[#A50710] text-black font-semibold text-sm disabled:opacity-40"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Submit & request callback"}
      </button>
      <p className="text-[10px] text-zinc-600 leading-snug text-center">
        We may receive commissions from lenders we introduce — see <a href="/credit-guide" className="underline">Credit Guide</a>.
      </p>
    </form>
  );
}

/**
 * Hoisted to module scope so its identity is stable across renders — otherwise
 * every keystroke re-creates the component and React unmounts/remounts the
 * <input>, dropping keyboard focus after every character.
 */
function ChatLeadField({ label, k, type = "text", placeholder, required, value, onChange }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}{required && " *"}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={`chat-lead-${k}-input`}
        className="mt-1 w-full bg-[#0c0c0c] border border-zinc-800 focus:border-[#E50914]/60 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none"
      />
    </label>
  );
}
